import { Shield } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "components/common/Sheet";
import {
  formatBenchmarkRangeLabel,
  formatBenchmarkSourceLabel,
  resolveBudgetBenchmarkMeta,
} from "../../budgetBenchmarkDetails";
import { getProductionTypeLabel, normalizeProductionType } from "../../budgetBenchmarks";
import type { BenchmarkDetailsSheetProps } from "./BenchmarkDetailsSheet.types";

export const BenchmarkDetailsSheet = ({
  open,
  onOpenChange,
  meta,
  productionType,
  source,
  benchmarkSet,
}: BenchmarkDetailsSheetProps) => {
  const resolvedMeta = resolveBudgetBenchmarkMeta(meta);
  const normalizedProductionType = normalizeProductionType(productionType);
  const productionLabel = getProductionTypeLabel(normalizedProductionType);
  const productionDefinition = resolvedMeta.production_types.find(
    (entry) => entry.key === normalizedProductionType
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col border-border bg-background p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-border px-5 py-4 text-left">
          <SheetTitle className="text-foreground">How budget benchmarks work</SheetTitle>
          <SheetDescription className="text-muted-foreground">
            {resolvedMeta.methodology.summary}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-5 py-4">
          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Your data stays private
            </h3>
            <div className="flex gap-2 rounded-md border border-border bg-muted/50 px-3 py-2.5">
              <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <p className="text-[11px] leading-relaxed text-foreground/80">
                {resolvedMeta.methodology.privacy_notice}
              </p>
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              How it works
            </h3>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              {resolvedMeta.methodology.how_it_works}
            </p>
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Baseline
            </h3>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              {formatBenchmarkSourceLabel(source)} for {productionLabel}.
            </p>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              {source === "peers"
                ? resolvedMeta.methodology.peer_source
                : resolvedMeta.methodology.industry_source}
            </p>
            {productionDefinition ? (
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                {productionDefinition.description}
              </p>
            ) : null}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Category buckets
            </h3>
            <div className="flex flex-col gap-2">
              {resolvedMeta.buckets.map((bucket) => (
                <div
                  key={bucket.bucket}
                  className="rounded-md border border-border bg-muted/40 px-3 py-2"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[11px] font-medium text-foreground">{bucket.label}</span>
                    {benchmarkSet ? (
                      <span className="text-[10px] tabular-nums text-muted-foreground">
                        {formatBenchmarkRangeLabel(bucket.bucket, benchmarkSet)}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
                    {bucket.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <p className="text-[10px] text-muted-foreground/60">
            Catalog v{resolvedMeta.version}
            {resolvedMeta.updated_at
              ? ` · updated ${new Date(resolvedMeta.updated_at).toLocaleDateString()}`
              : ""}
            {source === "peers"
              ? ` · peer minimum ${resolvedMeta.min_peer_sample_size} projects`
              : ""}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
